import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function QaOverview() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => { if (companyId) fetchProjects(); }, [companyId]);

  const authFetch = async (url, opts = {}) => {
    const res = await fetch(url, { ...opts, headers: { ...getAuthHeaders(), ...(opts.headers || {}) } });
    if (res.status === 401 || res.status === 403) { navigate('/login'); return null; }
    return res;
  };

  const fetchProjects = async () => {
    setLoading(true);
    const res = await authFetch(`${API}/companies/${companyId}/qa/overview`);
    if (res && res.ok) {
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : []);
    }
    setLoading(false);
  };

  const createProject = async () => {
    if (!newName.trim()) return;
    const res = await authFetch(`${API}/companies/${companyId}/qa/projects`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: newName })
    });
    if (res && res.ok) { setShowCreate(false); setNewName(''); fetchProjects(); }
    else if (res) { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return 'No activity';
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (!companyId) return <div style={s.page}><p>Please select a company first.</p></div>;
  if (initialLoading) return <div style={s.page}><p>Loading QA Overview...</p></div>;

  return (
    <div style={s.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={s.title}>QA Management</h2>
        <button onClick={() => setShowCreate(true)} style={s.btnPrimary}>+ New Project</button>
      </div>

      {projects.length === 0 && <p style={{ color: '#999' }}>No QA projects yet. Create one to get started.</p>}

      <div style={s.cardsGrid}>
        {projects.map(p => (
          <div key={p.id} style={{ ...s.card, cursor: 'pointer', borderLeft: `4px solid ${p.status === 'solved' ? '#27ae60' : '#e74c3c'}` }}
               onClick={() => navigate(`/company/qa/projects/${p.id}`)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{p.name}</h3>
              <span style={{ ...s.badge, backgroundColor: p.status === 'solved' ? '#27ae60' : '#e74c3c' }}>
                {p.status.toUpperCase()}
              </span>
            </div>
            <div style={s.counters}>
              <span style={{ color: '#e74c3c', fontWeight: 600 }}>{p.unsolved_count} Unsolved</span>
              <span style={{ color: '#27ae60', fontWeight: 600 }}>{p.solved_count} Solved</span>
              <span style={{ color: '#8e44ad', fontWeight: 600 }}>{p.critical_count} Critical</span>
            </div>
            <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
              Last activity: {timeAgo(p.last_activity)}
            </div>
          </div>
        ))}
      </div>

      {showCreate && (
        <div style={s.overlay} onClick={() => setShowCreate(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px' }}>New QA Project</h3>
            <label style={s.label}>Project Name *</label>
            <input value={newName} onChange={e => setNewName(e.target.value)} style={s.input} placeholder="e.g. EGETY Wallet"
                   onKeyDown={e => e.key === 'Enter' && createProject()} autoFocus />
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={createProject} style={s.btnPrimary}>Create</button>
              <button onClick={() => setShowCreate(false)} style={s.btnCancel}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: 24, maxWidth: 1200, margin: '0 auto' },
  title: { fontSize: 24, fontWeight: 700, margin: 0 },
  cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 },
  card: { background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  badge: { display: 'inline-block', padding: '3px 10px', borderRadius: 12, color: '#fff', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' },
  counters: { display: 'flex', gap: 16, fontSize: 13 },
  btnPrimary: { padding: '8px 18px', background: '#3498db', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  btnCancel: { padding: '8px 18px', background: '#ecf0f1', color: '#2c3e50', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, padding: 24, width: '90%', maxWidth: 450 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 },
  input: { padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, width: '100%', boxSizing: 'border-box' },
};  